import { getTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPFontIcon, IPStack, IPText } from 'baseComponents';
import { globalFontBoldWeight, globalStackTokensGapSmall, globalTextStylesDisabled } from 'globalStyles';
import * as React from 'react';
import { useContext } from 'react';

export interface ICardToggleOption {
  key: string | number;
  text: string;
  iconName?: string | undefined;
  subText?: string | undefined;
  disabled?: boolean;
}

export interface ICardToggleProps {
  options: ICardToggleOption[];
  selectedKeys?: (string | number)[];
  cardWidth?: number;
  cardHeight?: number;
  multiSelect?: boolean;
  allowEmptySelection?: boolean;
  compact?: boolean;
  maxColumns?: number;
  onSelect: (selectedKeys: (string | number)[]) => void;
}

export const CardToggle = (props: ICardToggleProps) => {
  const appContext = useContext(AppContext);
  const theme = getTheme();
  const [hoveredKey, setHoveredKey] = React.useState<string | number | undefined>(undefined);

  const multiSelect = props.multiSelect ?? false;
  const allowEmptySelection = props.allowEmptySelection ?? false;
  const selectedKeys = props.selectedKeys ?? [];

  const cardWidth = props.cardWidth ?? 240;
  const cardHeight = props.cardHeight ?? (props.compact ? 80 : 200);
  const cardGap = 12;
  const maxColumns = props.maxColumns ?? 3;
  const gridMaxWidth = maxColumns * cardWidth + (maxColumns - 1) * cardGap;

  const toggleSelection = (option: ICardToggleOption) => {
    const isSelected = selectedKeys.includes(option.key);
    let nextSelectedKeys: (string | number)[] = selectedKeys;

    if (multiSelect) {
      if (isSelected) {
        if (selectedKeys.length === 1 && !allowEmptySelection) {
          return;
        }

        nextSelectedKeys = selectedKeys.filter((key) => key !== option.key);
      } else {
        nextSelectedKeys = [...selectedKeys, option.key];
      }
    } else {
      if (isSelected) {
        if (!allowEmptySelection) {
          return;
        }

        nextSelectedKeys = [];
      } else {
        nextSelectedKeys = [option.key];
      }
    }

    props.onSelect(nextSelectedKeys);
  };

  const getCardItemRootStyle = () => ({
    width: '100%',
    minWidth: 0,
    minHeight: cardHeight,
    maxHeight: cardHeight,
  });

  const getCardRootStyle = (isSelected: boolean, isHovered: boolean, isDisabled: boolean) => {
    const activeBorderColor = appContext.useDarkMode ? theme.palette.themeSecondary : theme.palette.themePrimary;
    const showHoverStyle = isHovered && !isSelected;

    return {
      position: 'relative' as const,
      padding: 16,
      borderRadius: 8,
      border: `1px solid ${isHovered || isSelected ? activeBorderColor : theme.palette.neutralLight}`,
      boxShadow: showHoverStyle ? `0 0 0 2px ${activeBorderColor}1f, 0 3px 10px ${activeBorderColor}1a` : 'none',
      background: isSelected
        ? appContext.useDarkMode
          ? theme.palette.themeDarker
          : theme.palette.themeLighter
        : undefined,
      boxSizing: 'border-box' as const,
      minHeight: '100%',
      maxHeight: '100%',
      height: '100%',
      width: '100%',
      cursor: isDisabled ? undefined : 'pointer',
      transition: isSelected
        ? undefined
        : 'border-color 300ms ease, box-shadow 300ms ease, background-color 300ms ease',
    };
  };

  const getCardInteractionProps = (option: ICardToggleOption) => ({
    tabIndex: 0,
    onClick: () => toggleSelection(option),
    onMouseEnter: () => setHoveredKey(option.key),
    onMouseLeave: () => setHoveredKey(undefined),
    onFocus: () => setHoveredKey(option.key),
    onBlur: () => setHoveredKey(undefined),
    onKeyDown: (ev: React.KeyboardEvent<HTMLElement>) => {
      if (ev.key === 'Enter' || ev.key === ' ') {
        ev.preventDefault();
        toggleSelection(option);
      }
    },
  });

  const renderCompact = (option: ICardToggleOption, isSelected: boolean, isHovered: boolean, disabled: boolean) => {
    const interactionProps = disabled ? undefined : getCardInteractionProps(option);

    return (
      <div
        key={option.key}
        style={{
          width: cardWidth,
          minWidth: cardWidth,
          maxWidth: cardWidth,
        }}
      >
        <IPStack
          {...interactionProps}
          tokens={globalStackTokensGapSmall}
          horizontalAlign="stretch"
          styles={{
            root: {
              ...getCardItemRootStyle(),
              ...getCardRootStyle(isSelected, isHovered, disabled),
            },
          }}
        >
          <IPStack horizontal horizontalAlign="space-between" verticalAlign="start">
            <IPStack.Item>
              <IPText
                variant="mediumPlus"
                styles={{
                  root: {
                    fontWeight: globalFontBoldWeight,
                    color: disabled ? theme.palette.neutralTertiary : undefined,
                  },
                }}
              >
                {option.text}
              </IPText>
            </IPStack.Item>
            <IPStack.Item>
              <IPStack
                horizontalAlign="center"
                verticalAlign="center"
                styles={{
                  root: {
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    background: isSelected ? theme.palette.themePrimary : undefined,
                    display: 'flex',
                  },
                }}
              >
                {isSelected && (
                  <IPFontIcon
                    iconName="CheckMark"
                    style={{
                      color: '#FFFFFF',
                      fontSize: 10,
                    }}
                  />
                )}
              </IPStack>
            </IPStack.Item>
          </IPStack>

          <IPStack.Item>
            <IPText block nowrap variant="small" styles={globalTextStylesDisabled}>
              {option.subText}
            </IPText>
          </IPStack.Item>
        </IPStack>
      </div>
    );
  };

  const renderRegular = (option: ICardToggleOption, isSelected: boolean, isHovered: boolean, disabled: boolean) => {
    const interactionProps = disabled ? undefined : getCardInteractionProps(option);

    return (
      <div
        key={option.key}
        style={{
          width: cardWidth,
          minWidth: cardWidth,
          maxWidth: cardWidth,
        }}
      >
        <IPStack
          {...interactionProps}
          tokens={globalStackTokensGapSmall}
          horizontalAlign="stretch"
          styles={{
            root: {
              ...getCardItemRootStyle(),
              ...getCardRootStyle(isSelected, isHovered, disabled),
            },
          }}
        >
          <IPStack horizontal horizontalAlign="space-between" verticalAlign="center">
            {option.iconName && (
              <IPStack.Item>
                <IPStack
                  horizontalAlign="center"
                  verticalAlign="center"
                  styles={{
                    root: {
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: appContext.useDarkMode ? theme.palette.themeDarkAlt : theme.palette.themeLighterAlt,
                      display: 'flex',
                    },
                  }}
                >
                  <IPFontIcon
                    iconName={option.iconName}
                    style={{
                      fontSize: 16,
                    }}
                  />
                </IPStack>
              </IPStack.Item>
            )}

            {isSelected && (
              <IPStack.Item>
                <IPStack
                  horizontalAlign="center"
                  verticalAlign="center"
                  styles={{
                    root: {
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: theme.palette.themePrimary,
                      display: 'flex',
                    },
                  }}
                >
                  <IPFontIcon
                    iconName="CheckMark"
                    style={{
                      color: '#FFFFFF',
                      fontSize: 10,
                    }}
                  />
                </IPStack>
              </IPStack.Item>
            )}
          </IPStack>

          <IPStack tokens={globalStackTokensGapSmall}>
            <IPText
              variant="mediumPlus"
              styles={{
                root: {
                  fontWeight: globalFontBoldWeight,
                  color: disabled ? theme.palette.neutralTertiary : undefined,
                },
              }}
            >
              {option.text}
            </IPText>
            <IPText variant="medium">{option.subText}</IPText>
          </IPStack>
        </IPStack>
      </div>
    );
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fit, minmax(${cardWidth}px, ${cardWidth}px))`,
        justifyContent: 'center',
        gap: cardGap,
        width: '100%',
        maxWidth: gridMaxWidth,
        margin: '0 auto',
      }}
    >
      {props.options.map((option) => {
        const isDisabled = option.disabled ?? false;
        const isSelected = selectedKeys.includes(option.key);
        const isHovered = !isDisabled && option.key === hoveredKey;

        return props.compact
          ? renderCompact(option, isSelected, isHovered, isDisabled)
          : renderRegular(option, isSelected, isHovered, isDisabled);
      })}
    </div>
  );
};
